import { trackMediaPlay } from '../../utils/trackMediaPlay.js';

// Use the global FlashMessageManager that's already initialized.
// If not available, create a simple one with the same API.
const flashManager = window.flashMessageManager || {
  showSuccess: (msg, duration) => createFlashMessage(msg, 'success', duration || 5000),
  showError: (msg, duration) => createFlashMessage(msg, 'error', duration || 8000),
  showWarning: (msg, duration) => createFlashMessage(msg, 'warning', duration || 6500),
  showInfo: (msg, duration) => createFlashMessage(msg, 'info', duration || 5000)
};

// Fallback: simple flash message creation
function createFlashMessage(message, type, duration) {
  const container = document.querySelector('.flash-messages-container') || createContainer();
  const messageEl = document.createElement('div');
  messageEl.className = `flash-message flash-message-${type}`;
  messageEl.setAttribute('data-flash-type', type);
  messageEl.setAttribute('role', 'alert');

  const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
  const content = document.createElement('div');
  content.className = 'flash-content';

  const icon = document.createElement('span');
  icon.className = 'flash-icon';
  icon.textContent = icons[type] || '✓';

  const text = document.createElement('span');
  text.className = 'flash-text';
  text.textContent = message;

  const closeBtn = document.createElement('button');
  closeBtn.className = 'flash-close';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close notification');
  closeBtn.textContent = '×';

  content.appendChild(icon);
  content.appendChild(text);
  content.appendChild(closeBtn);
  messageEl.appendChild(content);
  container.appendChild(messageEl);

  requestAnimationFrame(() => messageEl.classList.add('flash-message-show'));

  closeBtn.addEventListener('click', () => {
    messageEl.classList.remove('flash-message-show');
    messageEl.classList.add('flash-message-hide');
    setTimeout(() => messageEl.remove(), 300);
  });

  if (duration > 0) {
    setTimeout(() => {
      if (messageEl.parentElement) {
        messageEl.classList.add('flash-message-hide');
        setTimeout(() => messageEl.remove(), 300);
      }
    }, duration);
  }

  return messageEl;
}

function createContainer() {
  const container = document.createElement('div');
  container.className = 'flash-messages-container flash-messages-top-right';
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-atomic', 'true');
  document.body.appendChild(container);
  return container;
}

function getCsrfToken() {
  return document.querySelector('input[name="_csrf"]')?.value || '';
}

async function postJSON(url, body) {
  const csrfToken = getCsrfToken();
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify(body)
  });
}

function tryGetFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return '';

  const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    const value = filenameStarMatch[1].trim();
    const parts = value.split("''");
    const encoded = parts.length > 1 ? parts.slice(1).join("''") : value;
    try {
      return decodeURIComponent(encoded.replace(/^"+|"+$/g, ''));
    } catch {
      return encoded.replace(/^"+|"+$/g, '');
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].trim().replace(/^"+|"+$/g, '');
  }

  return '';
}

// ─── Session user ─────────────────────────────────────────────────────────────

let currentUser = (() => {
  try {
    const stored = sessionStorage.getItem('currentUser');
    return stored ? JSON.parse(stored) : { firstName: '', email: '' };
  } catch {
    return { firstName: '', email: '' };
  }
})();

function setCurrentUser({ firstName, email }) {
  currentUser.firstName = firstName || '';
  currentUser.email = email || '';
  try {
    sessionStorage.setItem('currentUser', JSON.stringify({ firstName: currentUser.firstName, email: currentUser.email }));
  } catch { /* ignore storage failures */ }
}

function isSignedIn() {
  return !!currentUser.email;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function showSigninModal() {
  const modal = document.getElementById('freebies-signin-modal');
  if (!modal) return;
  modal.style.display = 'flex';
  document.body.style.overflow = 'hidden';
  setTimeout(() => modal.querySelector('input')?.focus(), 100);
}

function closeSigninModal() {
  const modal = document.getElementById('freebies-signin-modal');
  if (!modal) return;
  modal.style.display = 'none';
  document.body.style.overflow = '';
}

// ─── Download ─────────────────────────────────────────────────────────────────

// Stored when a download is intercepted to show the sign-in modal
let pendingDownload = null;

async function executeDownload(btn, freebieId, code) {
  const originalText = btn.textContent;
  try {
    btn.disabled = true;
    btn.textContent = 'Downloading...';

    const response = await postJSON('/resources/freebies/download', {
      freebieId,
      code,
      firstName: currentUser.firstName || '',
      email: currentUser.email || ''
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers.get('content-disposition');
      a.download = tryGetFilenameFromContentDisposition(contentDisposition) || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      flashManager.showSuccess('Download completed. Check your download directory.');
    } else {
      let errorMessage = 'Download failed.';
      const contentType = response.headers.get('content-type') || '';
      try {
        if (contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error?.message || errorMessage;
        } else {
          const text = await response.text();
          if (text) errorMessage = text;
        }
      } catch { /* keep default */ }
      flashManager.showError(errorMessage);
    }
  } catch (error) {
    console.error('Download error:', error);
    flashManager.showError('An error occurred during download.');
  } finally {
    btn.disabled = false;
    btn.textContent = originalText;
  }
}

// ─── Main setup ───────────────────────────────────────────────────────────────

function setupFormHandling() {
  const signupForm = document.getElementById('freebies-signin-form');

  if (!signupForm) {
    console.error('❌ Sign-in form not found. Check that form ID is "freebies-signin-form"');
    return;
  }

  const submitBtn = signupForm.querySelector('button[type="submit"]');

  // Close modal on backdrop click or close button
  document.getElementById('freebies-signin-modal-backdrop')
    ?.addEventListener('click', closeSigninModal);
  document.getElementById('freebies-signin-modal-close')
    ?.addEventListener('click', closeSigninModal);

  // Close modal on Escape key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeSigninModal();
  });

  // Sign-in form submission
  signupForm.addEventListener('submit', async function (e) {
    e.preventDefault();
    e.stopPropagation();

    const firstNameInput = document.getElementById('freebies-signin-form-firstName');
    const lastNameInput  = document.getElementById('freebies-signin-form-lastName');
    const emailInput     = document.getElementById('freebies-signin-form-email');

    if (!firstNameInput || !emailInput) {
      flashManager.showError('Form elements not found. Please refresh the page.');
      return;
    }

    const firstName = firstNameInput.value.trim();
    const lastName  = lastNameInput ? lastNameInput.value.trim() : '';
    const email     = emailInput.value.trim();

    if (!firstName || !email) {
      flashManager.showError('First name and email are required.');
      return;
    }

    const originalText = submitBtn ? submitBtn.textContent : '';
    try {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Processing...';
      }

      const response = await postJSON('/resources/freebies/signin', { firstName, lastName, email });
      let data = null;
      try { data = await response.json(); } catch { /* not JSON */ }

      if (response.ok && data?.success) {
        setCurrentUser({ firstName, email });
        closeSigninModal();
        flashManager.showSuccess(data.message || 'Signed in successfully.');

        // Fire the queued download
        if (pendingDownload) {
          const { btn, freebieId, code } = pendingDownload;
          pendingDownload = null;
          await executeDownload(btn, freebieId, code);
        }
      } else {
        flashManager.showError(data?.message || 'Sign-in failed.');
      }
    } catch (error) {
      console.error('Sign-in error:', error);
      flashManager.showError('An error occurred. Please try again.');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }, true);

  // Download buttons
  document.querySelectorAll('.freebie-item__download-btn').forEach(btn => {
    btn.addEventListener('click', async function () {
      if (this.hasAttribute('data-video-id')) return;

      const freebieId    = this.getAttribute('data-freebie-id');
      const requiresCode = this.getAttribute('data-requires-code') === 'true';

      if (!freebieId) {
        flashManager.showError('Missing resource ID. Please refresh the page.');
        return;
      }

      let code = '';
      if (requiresCode) {
        const codeInput = document.getElementById(`code-${freebieId}`);
        if (!codeInput) {
          flashManager.showError('Code field not found. Please refresh the page.');
          return;
        }
        code = codeInput.value.trim();
        if (!code) {
          flashManager.showError('Please enter a code to download this resource.');
          return;
        }
      }

      // First download in session — capture and show sign-in modal
      if (!isSignedIn()) {
        pendingDownload = { btn: this, freebieId, code };
        showSigninModal();
        return;
      }

      await executeDownload(this, freebieId, code);
    });
  });

  // Test start buttons
  document.querySelectorAll('.btn-start-test').forEach(btn => {
    btn.addEventListener('click', async function () {
      const testId   = this.getAttribute('data-test-id');
      const testSlug = this.getAttribute('data-test-slug');
      const codeInput = document.getElementById(`test-code-${testId}`);
      const errorEl   = codeInput?.parentElement?.querySelector('.error-message');

      if (!testId || !testSlug) {
        flashManager.showError('Invalid test configuration. Please refresh the page.');
        return;
      }

      const code = codeInput?.value?.trim() || '';
      if (!code) {
        if (errorEl) {
          errorEl.textContent = 'Please enter an access code';
          errorEl.classList.add('show');
        }
        flashManager.showWarning('Please enter the access code provided by your care provider.');
        codeInput?.focus();
        return;
      }

      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('show');
      }

      const originalText = this.textContent;
      try {
        this.disabled = true;
        this.textContent = 'Validating...';

        const response = await fetch(`/api/client-testing/tests/${testSlug}?code=${encodeURIComponent(code)}`);
        const data = await response.json();

        if (response.ok && data?.success) {
          flashManager.showSuccess('Access code validated! Redirecting to assessment...');
          try {
            sessionStorage.setItem('testSession', JSON.stringify({
              testSlug, code, user: currentUser, startedAt: new Date().toISOString()
            }));
          } catch { /* ignore */ }
          setTimeout(() => {
            window.location.href = `/resources/freebies/testing/${testSlug}?code=${encodeURIComponent(code)}`;
          }, 1000);
        } else {
          if (errorEl) {
            errorEl.textContent = data?.message || 'Invalid access code';
            errorEl.classList.add('show');
          }
          flashManager.showError(data?.message || 'Invalid access code. Please check and try again.');
          this.disabled = false;
          this.textContent = originalText;
        }
      } catch (error) {
        console.error('Test validation error:', error);
        flashManager.showError('An error occurred. Please try again.');
        this.disabled = false;
        this.textContent = originalText;
      }
    });
  });
}

// ─── Video Modal ──────────────────────────────────────────────────────────────

function openVideoModal(videoId, title) {
  const modal = document.getElementById('freebie-video-modal');
  const iframe = document.getElementById('freebie-video-modal-iframe');
  const titleEl = document.getElementById('freebie-video-modal-title');
  if (!modal || !iframe) return;
  if (titleEl && title) titleEl.textContent = title;
  iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  modal.style.display = 'flex';
  trackMediaPlay(`freebie:${videoId}`);
  document.body.style.overflow = 'hidden';
}

function closeVideoModal() {
  const modal = document.getElementById('freebie-video-modal');
  const iframe = document.getElementById('freebie-video-modal-iframe');
  if (!modal) return;
  if (iframe) iframe.src = '';
  modal.style.display = 'none';
  document.body.style.overflow = '';
}

function setupVideoModal() {
  document.getElementById('freebie-video-modal-backdrop')
    ?.addEventListener('click', closeVideoModal);
  document.getElementById('freebie-video-modal-close')
    ?.addEventListener('click', closeVideoModal);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeVideoModal();
  });

  document.querySelectorAll('[data-video-id]').forEach(btn => {
    btn.addEventListener('click', function () {
      openVideoModal(this.getAttribute('data-video-id'), this.getAttribute('data-video-title'));
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupFormHandling();
    setupVideoModal();
  });
} else {
  setupFormHandling();
  setupVideoModal();
}
